import { OnChanges, ViewEncapsulation, ElementRef, ViewChild, Renderer2, Injector } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, NgModel, ValidationErrors } from '@angular/forms';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { IFormQuestion, IFormAnswer, IFormViewer } from '@fi-shared/fi-form-shared/fi-form-shared';
import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { Observable } from 'rxjs';
import { FiImageSize, ObjectTypes } from '@fi-shared/types';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { isDefined } from '@fi-shared/utils';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { IInsertMediaResult } from '@fi-shared/fi-insert-media/fi-insert-media';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';

@Component({
    selector: 'fi-form-viewer-question',
    templateUrl: './fi-form-viewer-question.component.html',
    styleUrls: ['./fi-form-viewer-question.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-form-viewer-question' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiFormViewerQuestionComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiFormViewerQuestionComponent,
        },
    ],
    standalone: false,
})
export class FiFormViewerQuestionComponent
    extends ControlValueAccessorValidatorBase<IFormAnswer>
    implements OnInit, OnChanges
{
    @Input() question: IFormQuestion;
    @Input() formViewer: IFormViewer;
    @Input() isHidden: boolean;
    @Input() isFormDisabled: boolean;
    @Input() selectedLanguageId: number;
    @Input() isNoTopMarginHeadingElement: boolean;
    @Input() isMobileView: boolean = false;
    @ViewChild('textareaWrapper', { static: false }) textareaWrapperRef: ElementRef;
    answer: IFormAnswer;
    FiImageSize = FiImageSize;

    constructor(
        injector: Injector,
        private formHelperService: FiFormHelperService,
        private renderer: Renderer2,
        private insertMedia2Service: FiInsertMedia2Service,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.answer = this.ngModel;
        if (this.isSingleOrMultipleChoiceQuestion() && this.answer.ValuePresetOptionIds === null) {
            this.answer.ValuePresetOptionIds = [];
        }
        if (this.isTextQuestion() && this.answer.Value) {
            setTimeout(() => {
                this.setTextAreaHeight();
            });
        }

        // Initialize the hidden field value based on existing selections
        this.hasSelectedMultipleChoiceOptions =
            this.answer.ValuePresetOptionIds && !!this.answer.ValuePresetOptionIds.length;
    }

    isPlainViewMode$(): Observable<boolean> {
        return this.formHelperService.plainViewMode$;
    }

    doValidate(): ValidationErrors {
        return {
            required:
                this.question.IsMandatory &&
                !(
                    this.answer.Value ||
                    this.answer.ValuePresetOptionIds.length ||
                    this.answer.MediaBankFileReferences.length ||
                    this.isHidden
                ),
        };
    }

    setTextAreaHeight() {
        let textareaElement = this.textareaWrapperRef.nativeElement.children[0];
        this.renderer.setStyle(textareaElement, 'height', '24px');
        this.renderer.setStyle(textareaElement, 'height', `${textareaElement.scrollHeight}px`);
        this.doPropagateChanges();
    }

    ngOnChanges() {
        this.doPropagateChanges();
    }

    isAnswered(optionId: number): boolean {
        return (
            !!this.answer.ValuePresetOptionIds?.length &&
            isDefined(this.answer.ValuePresetOptionIds.find((id: number) => id === optionId))
        );
    }

    doPropagateChanges() {
        super.doPropagateChanges();
    }

    getPreferredContentLabelValue(items: IContentLanguageLabelModel[] | undefined): string {
        if (items && items.length) {
            const preferredContentLabel = items.find((item) => item.ContentLanguageId === this.selectedLanguageId);
            return preferredContentLabel ? preferredContentLabel.Value : '';
        }
        return '';
    }

    isTextOrNumericQuestion(): boolean {
        return this.formHelperService.isTextOrNumericQuestion(this.question);
    }

    isNumericQuestion(): boolean {
        return this.formHelperService.isNumericQuestion(this.question);
    }

    isHeadingElement(): boolean {
        return this.formHelperService.isHeadingElement(this.question);
    }

    isTextQuestion(): boolean {
        return this.formHelperService.isTextQuestion(this.question);
    }

    isSingleOrMultipleChoiceQuestion(): boolean {
        return this.formHelperService.isChoiceQuestion(this.question);
    }

    isSingleChoiceQuestion(): boolean {
        return this.formHelperService.isSingleChoiceQuestion(this.question);
    }

    isTextMessageElement(): boolean {
        return this.formHelperService.isTextMessageElement(this.question);
    }

    isMediaBankElement(): boolean {
        return this.formHelperService.isMediaBankElement(this.question);
    }

    isFileAttachmentElement(): boolean {
        return this.formHelperService.isFileAttachmentElement(this.question);
    }

    getModelForSingleChoiceQuestion(answer: IFormAnswer): SingleChoiceQuestionModel {
        return new SingleChoiceQuestionModel(answer);
    }

    getMediaBankFileReferencesForSelectedLanguage(question: IFormQuestion): IMediaBankFileReference[] {
        return question.MediaBankFileReferences.filter((mbRef: IMediaBankFileReference) => {
            return this.formHelperService.isMediaBankFileReferenceForSelectedLanguage(mbRef, this.selectedLanguageId);
        });
    }

    openInsertMediaDialog(filesToUpload?: File[]) {
        this.insertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.FormAnswer, { filesToUpload: filesToUpload })
            .onYesButtonClick((result: IInsertMediaResult) => {
                if (!this.answer.hasOwnProperty('MediaBankFileReferences')) {
                    this.answer.MediaBankFileReferences = [];
                }
                this.answer.MediaBankFileReferences = this.answer.MediaBankFileReferences.concat(
                    result.mediaBankFileReferences,
                );
                this.cdr.markForCheck();
            });
    }

    isFormAttachmentsShown(): boolean | number {
        return this.answer.MediaBankFileReferences && this.answer.MediaBankFileReferences.length;
    }

    fileAttachmentChange(mediaBankFileReferences: IMediaBankFileReference[]) {
        this.answer.MediaBankFileReferences = mediaBankFileReferences;
    }

    hasSelectedMultipleChoiceOptions: boolean = false;
    updateMultipleChoiceAnswer(optionId: any, isChecked: boolean, multipleChoiceAnswerModel: NgModel) {
        if (isNullOrUndefined(this.answer.ValuePresetOptionIds)) {
            this.answer.ValuePresetOptionIds = [];
        }

        if (isChecked) {
            if (!this.answer.ValuePresetOptionIds.includes(optionId)) {
                this.answer.ValuePresetOptionIds.push(optionId);
            }
        } else {
            this.answer.ValuePresetOptionIds = this.answer.ValuePresetOptionIds.filter((id) => id !== optionId);
        }
        this.hasSelectedMultipleChoiceOptions = !!this.answer.ValuePresetOptionIds.length;
        multipleChoiceAnswerModel.control.markAsTouched();
        multipleChoiceAnswerModel.control.markAsDirty();
    }
}

class SingleChoiceQuestionModel {
    constructor(private answer: IFormAnswer) {}
    get valuePresetOptionId(): number {
        return this.answer.ValuePresetOptionIds && this.answer.ValuePresetOptionIds.length
            ? this.answer.ValuePresetOptionIds[0]
            : null;
    }
    set valuePresetOptionId(value: number) {
        this.answer.ValuePresetOptionIds = value ? [value] : [];
    }
}
